import { useCallback, useEffect } from 'react';
import {
    doArchiveJob,
    doCreateJob,
    doDeleteJob,
    doEditJob,
    doGetJob,
    doGetJobInvoicesCount,
    doListJobs,
    doListTags,
    doUnarchiveJob,
    doUpdateExternalReferences,
} from 'store/jobs/action';
import { CreateJobPayload, GetJobsParams, JobResponse, UpdateExternalReferencesPayload } from 'store/jobs/job';
import { useAppDispatch, useAppSelector } from '../config/hooks';
import { EditJobPayload } from 'store/jobs/types';

interface UseJobsProps {
    jobId?: string;
    autoFetch?: boolean;
}

const useJobs = ({ jobId, autoFetch = true }: UseJobsProps = {}) => {
    const jobs = useAppSelector((state) => state.jobs.jobs);
    const job = useAppSelector((state) => (jobId ? state.jobs.entities[jobId] : ({} as JobResponse)));
    const isLoading = useAppSelector((state) => state.jobs.isLoading);
    const error = useAppSelector((state) => state.jobs.error);
    const tagsList = useAppSelector((state) => state.jobs.tags);
    const invoicesCount = useAppSelector((state) => (jobId ? state.jobs.invoicesCounts[jobId] ?? 0 : 0));

    const dispatch = useAppDispatch();

    const createJob = useCallback((payload: CreateJobPayload) => dispatch(doCreateJob(payload)), [dispatch]);

    const listJobs = useCallback((params?: GetJobsParams) => dispatch(doListJobs(params)), [dispatch]);

    const getJob = useCallback((jobId: string) => dispatch(doGetJob(jobId)), [dispatch]);

    const editJob = useCallback(
        (jobId: string, payload: EditJobPayload) => dispatch(doEditJob(jobId, payload)),
        [dispatch]
    );

    const listTags = useCallback((params?: any) => dispatch(doListTags(params)), [dispatch]);
    const deleteJob = useCallback((jobId: string) => dispatch(doDeleteJob(jobId)), [dispatch]);
    const archiveJob = useCallback((jobId: string) => dispatch(doArchiveJob(jobId)), [dispatch]);
    const unarchiveJob = useCallback((jobId: string) => dispatch(doUnarchiveJob(jobId)), [dispatch]);
    const updateExternalReferences = useCallback(
        (jobId: string, payload: UpdateExternalReferencesPayload) =>
            dispatch(doUpdateExternalReferences(jobId, payload)),
        [dispatch]
    );
    const getJobInvoicesCount = useCallback((jobId: string) => dispatch(doGetJobInvoicesCount(jobId)), [dispatch]);

    useEffect(() => {
        if (jobId && autoFetch) {
            getJob(jobId);
        }
    }, [jobId, getJob, autoFetch]);

    return {
        isLoading,
        error,
        jobs,
        job,
        tagsList,
        invoicesCount,
        archiveJob,
        unarchiveJob,
        createJob,
        listJobs,
        getJob,
        editJob,
        listTags,
        deleteJob,
        updateExternalReferences,
        getJobInvoicesCount,
    };
};

export default useJobs;
